import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  Modal,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  Statistic,
  Divider,
  message
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  ExportOutlined,
  EyeOutlined,
  DollarOutlined,
  CalendarOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Types TypeScript
interface Transaction {
  id: number;
  company_id: number;
  subscription_plan_id: number;
  transaction_type: 'payment' | 'refund' | 'upgrade' | 'downgrade';
  amount: number;
  currency: string;
  payment_method: string;
  payment_reference: string | null;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  processed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  company_name: string;
  company_email: string;
  plan_name: string;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const TransactionHistory: React.FC = () => {
  // États du composant
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });

  // États pour les modals
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // États pour les statistiques
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    successfulTransactions: 0,
    failedTransactions: 0
  });

  const { hasPermission } = useAuth();

  // Charger les transactions
  const fetchTransactions = async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(typeFilter !== 'all' && { transaction_type: typeFilter }),
        ...(selectedCompany !== 'all' && { company_id: selectedCompany }),
        ...(dateRange && dateRange[0] && dateRange[1] && {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD')
        })
      });

      const response = await fetch(`/api/subscription/transactions?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setTransactions(data.data.transactions);
        setPagination(data.data.pagination);
        
        // Calculer les statistiques
        calculateStats(data.data.transactions);
      } else {
        message.error('Erreur lors du chargement des transactions');
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Calculer les statistiques
  const calculateStats = (transactionList: Transaction[]) => {
    const totalRevenue = transactionList
      .filter(t => t.transaction_type === 'payment' && t.status === 'completed')
      .reduce((sum, t) => sum + t.amount, 0);

    const successfulTransactions = transactionList.filter(t => t.status === 'completed').length;
    const failedTransactions = transactionList.filter(t => t.status === 'failed').length;

    setStats({
      totalRevenue,
      totalTransactions: transactionList.length,
      successfulTransactions,
      failedTransactions
    });
  };

  // Effet pour charger les données au montage
  useEffect(() => {
    if (hasPermission('Admin.Souscription.Transactions.view')) {
      fetchTransactions();
    }
  }, [hasPermission, statusFilter, typeFilter, selectedCompany, dateRange]);

  // Gestionnaire de filtres
  const handleSearch = () => {
    fetchTransactions(1, pagination.per_page);
  };

  // Gestionnaire de pagination
  const handlePageChange = (page: number, pageSize?: number) => {
    const newPageSize = pageSize || pagination.per_page;
    fetchTransactions(page, newPageSize);
  };

  // Afficher les détails d'une transaction
  const showTransactionDetail = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDetailModalVisible(true);
  };

  // Exporter les données
  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        format: 'csv',
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(typeFilter !== 'all' && { transaction_type: typeFilter }),
        ...(selectedCompany !== 'all' && { company_id: selectedCompany }),
        ...(dateRange && dateRange[0] && dateRange[1] && {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD')
        })
      });

      // Simuler l'export (vous pouvez implémenter un vrai endpoint d'export)
      message.success('Export en cours de préparation...');
      
      // Ici vous pourriez télécharger un fichier CSV
      
    } catch (error) {
      message.error('Erreur lors de l\'export');
    }
  };

  // Obtenir le badge de statut
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      completed: { color: 'success', text: 'Complété' },
      pending: { color: 'processing', text: 'En attente' },
      failed: { color: 'error', text: 'Échoué' },
      cancelled: { color: 'default', text: 'Annulé' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Obtenir le badge de type
  const getTypeBadge = (type: string) => {
    const typeConfig = {
      payment: { color: 'blue', text: 'Paiement', icon: <DollarOutlined /> },
      refund: { color: 'red', text: 'Remboursement', icon: <DollarOutlined /> },
      upgrade: { color: 'green', text: 'Mise à niveau', icon: <DollarOutlined /> },
      downgrade: { color: 'orange', text: 'Rétrogradation', icon: <DollarOutlined /> }
    };

    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.payment;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'ID Transaction',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: number) => (
        <Text code>#{id.toString().padStart(6, '0')}</Text>
      ),
    },
    {
      title: 'Entreprise',
      key: 'company',
      render: (_: any, record: Transaction) => (
        <div>
          <div className="font-medium">{record.company_name}</div>
          <div className="text-sm text-gray-500">{record.company_email}</div>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
      render: (planName: string) => (
        <Tag color="geekblue">{planName}</Tag>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      render: (type: string) => getTypeBadge(type),
    },
    {
      title: 'Montant',
      key: 'amount',
      render: (_: any, record: Transaction) => (
        <div className="text-right">
          <div className="font-bold text-lg">
            {record.amount.toLocaleString()} {record.currency}
          </div>
          <div className="text-sm text-gray-500">
            {record.payment_method}
          </div>
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusBadge(status),
    },
    {
      title: 'Date',
      key: 'date',
      render: (_: any, record: Transaction) => (
        <div>
          <div className="text-sm">
            {dayjs(record.created_at).format('DD/MM/YYYY')}
          </div>
          <div className="text-xs text-gray-500">
            {dayjs(record.created_at).format('HH:mm')}
          </div>
          {record.processed_at && record.status === 'completed' && (
            <div className="text-xs text-green-500">
              Traité: {dayjs(record.processed_at).format('DD/MM HH:mm')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: Transaction) => (
        <Tooltip title="Voir les détails">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => showTransactionDetail(record)}
          />
        </Tooltip>
      ),
    },
  ];

  // Vérification des permissions
  if (!hasPermission('Admin.Souscription.Transactions.view')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileTextOutlined className="text-4xl text-gray-400 mb-4" />
          <Title level={4} type="secondary">Accès non autorisé</Title>
          <Text type="secondary">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="mb-6">
        <Title level={2}>
          <FileTextOutlined className="mr-2" />
          Historique des Transactions
        </Title>
        <Text type="secondary">
          Consultez l'historique complet des transactions et paiements
        </Text>
      </div>

      {/* Statistiques */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Revenus Totaux"
              value={stats.totalRevenue}
              suffix="XOF"
              valueStyle={{ color: '#3f8600' }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Transactions"
              value={stats.totalTransactions}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Réussies"
              value={stats.successfulTransactions}
              valueStyle={{ color: '#52c41a' }}
              prefix={<Badge status="success" />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Échouées"
              value={stats.failedTransactions}
              valueStyle={{ color: '#f5222d' }}
              prefix={<Badge status="error" />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card className="mb-4">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Rechercher..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          
          <Col xs={24} sm={12} md={4}>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-full"
              placeholder="Statut"
            >
              <Option value="all">Tous</Option>
              <Option value="completed">Complétés</Option>
              <Option value="pending">En attente</Option>
              <Option value="failed">Échoués</Option>
              <Option value="cancelled">Annulés</Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={4}>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              className="w-full"
              placeholder="Type"
            >
              <Option value="all">Tous</Option>
              <Option value="payment">Paiements</Option>
              <Option value="refund">Remboursements</Option>
              <Option value="upgrade">Mises à niveau</Option>
              <Option value="downgrade">Rétrogradations</Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
              className="w-full"
              placeholder={['Date début', 'Date fin']}
            />
          </Col>
          
          <Col xs={24} sm={24} md={4}>
            <Space className="w-full">
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => fetchTransactions()}
                title="Actualiser"
              />
              
              <Button 
                icon={<ExportOutlined />} 
                onClick={handleExport}
                title="Exporter"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tableau des transactions */}
      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current_page,
            total: pagination.total,
            pageSize: pagination.per_page,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} sur ${total} transactions`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modal de détail */}
      <Modal
        title={
          <div className="flex items-center">
            <FileTextOutlined className="mr-2 text-primary" />
            Détails de la transaction #{selectedTransaction?.id.toString().padStart(6, '0')}
          </div>
        }
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Fermer
          </Button>
        ]}
        width={600}
      >
        {selectedTransaction && (
          <div className="space-y-4">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <Text type="secondary">Entreprise:</Text>
                  <div className="font-medium">{selectedTransaction.company_name}</div>
                  <div className="text-sm text-gray-500">{selectedTransaction.company_email}</div>
                </div>
              </Col>
              
              <Col span={12}>
                <div>
                  <Text type="secondary">Plan:</Text>
                  <div className="font-medium">{selectedTransaction.plan_name}</div>
                </div>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <div>
                  <Text type="secondary">Type:</Text>
                  <div>{getTypeBadge(selectedTransaction.transaction_type)}</div>
                </div>
              </Col>
              
              <Col span={8}>
                <div>
                  <Text type="secondary">Statut:</Text>
                  <div>{getStatusBadge(selectedTransaction.status)}</div>
                </div>
              </Col>
              
              <Col span={8}>
                <div>
                  <Text type="secondary">Montant:</Text>
                  <div className="font-bold text-lg">
                    {selectedTransaction.amount.toLocaleString()} {selectedTransaction.currency}
                  </div>
                </div>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <Text type="secondary">Méthode de paiement:</Text>
                  <div className="font-medium">{selectedTransaction.payment_method}</div>
                </div>
              </Col>
              
              <Col span={12}>
                <div>
                  <Text type="secondary">Référence:</Text>
                  <div className="font-medium">
                    {selectedTransaction.payment_reference || 'N/A'}
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <Text type="secondary">Date de création:</Text>
                  <div>{dayjs(selectedTransaction.created_at).format('DD/MM/YYYY HH:mm')}</div>
                </div>
              </Col>
              
              <Col span={12}>
                <div>
                  <Text type="secondary">Date de traitement:</Text>
                  <div>
                    {selectedTransaction.processed_at 
                      ? dayjs(selectedTransaction.processed_at).format('DD/MM/YYYY HH:mm')
                      : 'Non traité'
                    }
                  </div>
                </div>
              </Col>
            </Row>

            {selectedTransaction.notes && (
              <>
                <Divider />
                <div>
                  <Text type="secondary">Notes:</Text>
                  <div className="mt-2 p-3 bg-gray-50 rounded">
                    {selectedTransaction.notes}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TransactionHistory;